import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ODYSSEY_APP_ID } from "@forgepoint/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConnectionsStore } from "@/store/connectionsStore";
import {
  ODYSSEY_BASE_URL_PLACEHOLDER,
  buildOdysseyConnectionPayload,
  hasOdysseyRequiredFields,
} from "@/components/erp/odysseyConnectionForm";
import { toast } from "sonner";

interface OdysseyConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

/**
 * Dialog that collects the Odyssey API host, API key and plant and finalizes the connection.
 * @param props - Dialog open state and callbacks
 * @returns The connection dialog
 */
export function OdysseyConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
}: OdysseyConnectionDialogProps) {
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );
  const finalizeError = useConnectionsStore(
    (state) =>
      state.connectionStates.get(ODYSSEY_APP_ID)?.finalizeError ?? null,
  );
  const clearConnectionError = useConnectionsStore(
    (state) => state.clearConnectionError,
  );

  useEffect(() => {
    clearConnectionError(ODYSSEY_APP_ID);
    if (open) {
      return;
    }
    setBaseUrl("");
    setApiKey("");
    setCompanyId("");
    setError(null);
    setIsSubmitting(false);
  }, [open, clearConnectionError]);

  const handleSubmit = useCallback(async () => {
    const result = buildOdysseyConnectionPayload({
      baseUrl,
      apiKey,
      companyId,
    });
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const connection = await finalizeConnection(result.input);
      if (!connection) {
        const storedError = useConnectionsStore
          .getState()
          .connectionStates.get(ODYSSEY_APP_ID)?.finalizeError;
        setError(
          storedError ||
            "Odyssey connection validation failed. Check the base URL, API key and plant.",
        );
        return;
      }
      toast.success(
        `Connected to Odyssey plant ${result.input.config.companyId}.`,
      );
      await fetchConnectionsByApp(ODYSSEY_APP_ID);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create Odyssey connection.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    baseUrl,
    apiKey,
    companyId,
    fetchConnectionsByApp,
    finalizeConnection,
    onOpenChange,
    onSuccess,
  ]);

  const displayedError = error ?? finalizeError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Odyssey</DialogTitle>
          <DialogDescription>
            Enter the Odyssey API URL, API key, and plant.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="odyssey-base-url">Base URL</Label>
              <Input
                id="odyssey-base-url"
                inputMode="url"
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                placeholder={ODYSSEY_BASE_URL_PLACEHOLDER}
                autoComplete="off"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="odyssey-api-key">API Key</Label>
              <Input
                id="odyssey-api-key"
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="odyssey-company-id">Plant</Label>
              <Input
                id="odyssey-company-id"
                value={companyId}
                onChange={(event) => setCompanyId(event.target.value)}
                autoComplete="off"
              />
            </div>
            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !hasOdysseyRequiredFields({ baseUrl, apiKey, companyId })
              }
            >
              Connect
              {isSubmitting && (
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
